// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file Credit.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Gestão de Crédito e Contratos
// @date 2026-01-28
// ------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  LinearProgress,
} from '@mui/material';
import {
  CreditCard,
  TrendingUp,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  Visibility,
  ThumbUp,
  ThumbDown,
  Warning,
} from '@mui/icons-material';
import DataTable, { Column, StatusChip, CurrencyCell, DateCell } from '../../components/Common/DataTable';
import { useTranslation } from 'react-i18next';
import bankingService, { CreditApplication, CreditContract, CreditFilters } from '../../services/banking.service';

const APPLICATION_STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  pending: { label: 'Pendente', color: 'warning' },
  under_review: { label: 'Em Análise', color: 'info' },
  approved: { label: 'Aprovado', color: 'success' },
  rejected: { label: 'Rejeitado', color: 'error' },
  cancelled: { label: 'Cancelado', color: 'default' },
  disbursed: { label: 'Desembolsado', color: 'success' },
};

const CONTRACT_STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  active: { label: 'Ativo', color: 'success' },
  paid_off: { label: 'Liquidado', color: 'info' },
  overdue: { label: 'Em Atraso', color: 'error' },
  defaulted: { label: 'Inadimplente', color: 'error' },
  restructured: { label: 'Reestruturado', color: 'warning' },
};

const PRODUCT_TYPES: Record<string, string> = {
  personal_loan: 'Empréstimo Pessoal',
  credit_line: 'Linha de Crédito',
  overdraft: 'Cheque Especial',
  microcredit: 'Microcrédito',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const BankingCredit: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Applications state
  const [applications, setApplications] = useState<CreditApplication[]>([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [appPage, setAppPage] = useState(0);
  const [appRowsPerPage, setAppRowsPerPage] = useState(10);
  const [appTotalCount, setAppTotalCount] = useState(0);
  const [appStatusFilter, setAppStatusFilter] = useState('all');

  // Contracts state
  const [contracts, setContracts] = useState<CreditContract[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [contractPage, setContractPage] = useState(0);
  const [contractRowsPerPage, setContractRowsPerPage] = useState(10);
  const [contractTotalCount, setContractTotalCount] = useState(0);
  const [contractStatusFilter, setContractStatusFilter] = useState('all');

  // Dialog states
  const [selectedApp, setSelectedApp] = useState<CreditApplication | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approvedAmount, setApprovedAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalApps: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalRequested: 0,
    totalApproved: 0,
    activeContracts: 0,
    overdueContracts: 0,
    totalOutstanding: 0,
  });

  const fetchApplications = useCallback(async () => {
    setLoadingApps(true);
    try {
      const filters: CreditFilters = {
        page: appPage + 1,
        limit: appRowsPerPage,
        ...(appStatusFilter !== 'all' && { status: appStatusFilter }),
      };

      const response = await bankingService.getCreditApplications(filters);
      setApplications(response.data);
      setAppTotalCount(response.pagination?.total || 0);

      if (response.summary) {
        setStats(prev => ({
          ...prev,
          totalApps: response.summary.total,
          pending: response.summary.pending,
          approved: response.summary.approved,
          rejected: response.summary.rejected,
          totalRequested: response.summary.totalRequestedAmount,
          totalApproved: response.summary.totalApprovedAmount,
        }));
      }
    } catch (err: any) {
      setError(err.message || t('banking.credit.errorLoadingApps'));
    } finally {
      setLoadingApps(false);
    }
  }, [appPage, appRowsPerPage, appStatusFilter]);

  const fetchContracts = useCallback(async () => {
    setLoadingContracts(true);
    try {
      const filters: CreditFilters = {
        page: contractPage + 1,
        limit: contractRowsPerPage,
        ...(contractStatusFilter !== 'all' && { status: contractStatusFilter }),
      };

      const response = await bankingService.getCreditContracts(filters);
      setContracts(response.data);
      setContractTotalCount(response.pagination?.total || 0);

      if (response.summary) {
        setStats(prev => ({
          ...prev,
          activeContracts: response.summary.activeContracts,
          overdueContracts: response.summary.overdueContracts,
          totalOutstanding: response.summary.totalOutstanding,
        }));
      }
    } catch (err: any) {
      setError(err.message || t('banking.credit.errorLoadingContracts'));
    } finally {
      setLoadingContracts(false);
    }
  }, [contractPage, contractRowsPerPage, contractStatusFilter]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchApplications();
    } else {
      fetchContracts();
    }
  }, [tabValue, fetchApplications, fetchContracts]);

  const handleApprove = async () => {
    if (!selectedApp) return;
    setActionLoading(true);
    try {
      await bankingService.approveCreditApplication(selectedApp.id, approvedAmount, notes);
      setApproveOpen(false);
      fetchApplications();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApp || !rejectReason) return;
    setActionLoading(true);
    try {
      await bankingService.rejectCreditApplication(selectedApp.id, rejectReason);
      setRejectOpen(false);
      fetchApplications();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  const applicationColumns: Column<CreditApplication>[] = [
    { id: 'id', label: 'ID', format: (_, row) => row.id.slice(0, 8) + '...' },
    {
      id: 'customer',
      label: 'Cliente',
      format: (_, row) => (
        <Box>
          <Typography variant="body2">{row.customerName}</Typography>
          <Typography variant="caption" color="text.secondary">{row.customerPhone}</Typography>
        </Box>
      ),
    },
    {
      id: 'product',
      label: 'Produto',
      format: (_, row) => (
        <Box>
          <Typography variant="body2">{row.productName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {PRODUCT_TYPES[row.productType] || row.productType}
          </Typography>
        </Box>
      ),
    },
    { id: 'requestedAmount', label: 'Valor Solicitado', align: 'right', format: (_, row) => <CurrencyCell value={row.requestedAmount} /> },
    { id: 'term', label: 'Prazo', format: (_, row) => `${row.term} meses` },
    { id: 'interestRate', label: 'Taxa', format: (_, row) => `${row.interestRate}%` },
    {
      id: 'status',
      label: 'Estado',
      format: (_, row) => {
        const status = APPLICATION_STATUS[row.status];
        return <Chip label={status?.label || row.status} size="small" color={status?.color || 'default'} />;
      },
    },
    { id: 'submittedAt', label: 'Data', format: (_, row) => <DateCell value={row.submittedAt} /> },
    {
      id: 'actions',
      label: 'Ações',
      align: 'center',
      format: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Ver detalhes">
            <IconButton size="small" onClick={() => { setSelectedApp(row); setDetailsOpen(true); }}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {(row.status === 'pending' || row.status === 'under_review') && (
            <>
              <Tooltip title="Aprovar">
                <IconButton size="small" color="success" onClick={() => {
                  setSelectedApp(row);
                  setApprovedAmount(row.requestedAmount);
                  setApproveOpen(true);
                }}>
                  <ThumbUp fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rejeitar">
                <IconButton size="small" color="error" onClick={() => { setSelectedApp(row); setRejectOpen(true); }}>
                  <ThumbDown fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  const contractColumns: Column<CreditContract>[] = [
    { id: 'id', label: 'Contrato', format: (_, row) => row.id.slice(0, 8) + '...' },
    { id: 'customerName', label: 'Cliente' },
    { id: 'productName', label: 'Produto' },
    { id: 'principalAmount', label: 'Capital', align: 'right', format: (_, row) => <CurrencyCell value={row.principalAmount} /> },
    { id: 'outstandingBalance', label: 'Saldo Devedor', align: 'right', format: (_, row) => <CurrencyCell value={row.outstandingBalance} /> },
    { id: 'monthlyPayment', label: 'Prestação', align: 'right', format: (_, row) => <CurrencyCell value={row.monthlyPayment} /> },
    {
      id: 'progress',
      label: 'Progresso',
      format: (_, row) => {
        const progress = ((row.term - row.remainingInstallments) / row.term) * 100;
        return (
          <Box sx={{ width: 100 }}>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption">{row.paidInstallments}/{row.term}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'status',
      label: 'Estado',
      format: (_, row) => {
        const status = CONTRACT_STATUS[row.status];
        return (
          <Box>
            <Chip label={status?.label || row.status} size="small" color={status?.color || 'default'} />
            {row.daysOverdue && row.daysOverdue > 0 && (
              <Typography variant="caption" color="error" display="block">
                {row.daysOverdue} dias
              </Typography>
            )}
          </Box>
        );
      },
    },
    { id: 'nextPaymentDate', label: 'Próx. Pagamento', format: (_, row) => <DateCell value={row.nextPaymentDate} parede /> },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          {t('banking.credit.title')}
        </Typography>
        <Button startIcon={<Refresh />} variant="outlined" onClick={tabValue === 0 ? fetchApplications : fetchContracts}>
          {t('common.refresh')}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Pending color="warning" />
                <Typography color="text.secondary">{t('banking.credit.pending')}</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <Typography color="text.secondary">{t('banking.credit.approved')}</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{stats.approved}</Typography>
              <Typography variant="body2" color="text.secondary">{formatCurrency(stats.totalApproved)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCard color="primary" />
                <Typography color="text.secondary">{t('banking.credit.activeContracts')}</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{stats.activeContracts}</Typography>
              <Typography variant="body2" color="text.secondary">{formatCurrency(stats.totalOutstanding)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: stats.overdueContracts > 0 ? 'error.light' : undefined }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color={stats.overdueContracts > 0 ? 'error' : 'disabled'} />
                <Typography color="text.secondary">{t('banking.credit.overdue')}</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{stats.overdueContracts}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={t('banking.credit.applications')} />
          <Tab label={t('banking.credit.contracts')} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3 }}>
            <TextField
              size="small"
              select
              label="Estado"
              value={appStatusFilter}
              onChange={(e) => setAppStatusFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="pending">Pendente</MenuItem>
              <MenuItem value="under_review">Em Análise</MenuItem>
              <MenuItem value="approved">Aprovado</MenuItem>
              <MenuItem value="rejected">Rejeitado</MenuItem>
            </TextField>
          </Box>

          {loadingApps ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <DataTable
              columns={applicationColumns}
              data={applications}
              page={appPage}
              rowsPerPage={appRowsPerPage}
              totalCount={appTotalCount}
              onPageChange={setAppPage}
              onRowsPerPageChange={setAppRowsPerPage}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <TextField
              size="small"
              select
              label="Estado"
              value={contractStatusFilter}
              onChange={(e) => setContractStatusFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Ativo</MenuItem>
              <MenuItem value="overdue">Em Atraso</MenuItem>
              <MenuItem value="paid_off">Liquidado</MenuItem>
            </TextField>
          </Box>

          {loadingContracts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <DataTable
              columns={contractColumns}
              data={contracts}
              page={contractPage}
              rowsPerPage={contractRowsPerPage}
              totalCount={contractTotalCount}
              onPageChange={setContractPage}
              onRowsPerPageChange={setContractRowsPerPage}
            />
          )}
        </TabPanel>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('banking.credit.detailsTitle')}</DialogTitle>
        <DialogContent>
          {selectedApp && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 6 }}><Typography variant="caption">Cliente</Typography><Typography>{selectedApp.customerName}</Typography></Grid>
              <Grid size={{ xs: 6 }}><Typography variant="caption">Telefone</Typography><Typography>{selectedApp.customerPhone}</Typography></Grid>
              <Grid size={{ xs: 6 }}><Typography variant="caption">Produto</Typography><Typography>{selectedApp.productName}</Typography></Grid>
              <Grid size={{ xs: 6 }}><Typography variant="caption">Tipo</Typography><Typography>{PRODUCT_TYPES[selectedApp.productType]}</Typography></Grid>
              <Grid size={{ xs: 4 }}><Typography variant="caption">Valor</Typography><Typography variant="h6">{formatCurrency(selectedApp.requestedAmount)}</Typography></Grid>
              <Grid size={{ xs: 4 }}><Typography variant="caption">Prazo</Typography><Typography>{selectedApp.term} meses</Typography></Grid>
              <Grid size={{ xs: 4 }}><Typography variant="caption">Taxa</Typography><Typography>{selectedApp.interestRate}% a.m.</Typography></Grid>
              <Grid size={{ xs: 6 }}><Typography variant="caption">Nível KYC</Typography><Typography>{selectedApp.kycLevel}</Typography></Grid>
              <Grid size={{ xs: 6 }}><Typography variant="caption">Score</Typography><Typography>{selectedApp.creditScore || 'N/A'}</Typography></Grid>
              {selectedApp.rejectionReason && (
                <Grid size={{ xs: 12 }}><Alert severity="error">{selectedApp.rejectionReason}</Alert></Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailsOpen(false)}>{t('common.close')}</Button></DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)}>
        <DialogTitle>{t('banking.credit.approveTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label={t('banking.credit.approvedAmount')}
            value={approvedAmount}
            onChange={(e) => setApprovedAmount(Number(e.target.value))}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('banking.credit.observations')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={actionLoading}>{t('common.cancel')}</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : t('banking.credit.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}>
        <DialogTitle>{t('banking.credit.rejectTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('banking.credit.rejectReason')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={actionLoading}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading || !rejectReason}>
            {actionLoading ? <CircularProgress size={20} /> : t('banking.credit.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankingCredit;
